// src/types/ProgramModel.tsx - Updated based on actual question structure

import { EapProgram } from "./School";

export interface Program {
  id: string;
  name: EapProgram;
  weekCount: number;
  moduleCount: string;
  weeks?: ProgramWeek[];
}

export interface ProgramWeek {
  weekId: string;
  programId?: string;
  weekNumber: number;
  unlockDate?: string;
  startDate?: string;
  name: string;
  modules: Module[];
  status: ProgramWeekStatus;
  progress: number;
}

export interface Module {
  type: string;
  moduleId: string;
  name: string;
  description: string;
  learningContent: string;
  weekNumber?: number;
  questionCount: number;
  completed: boolean;
  score: number;
  attempts: number;
  questions?: ModuleQuestion[];
}

// Base question interface - matches your new structure
interface BaseQuestion {
  id: string;
  type: string;
  question: string;
  points: number;
  module: string;
  skill: string;
  week: number;
  program: string;
  asset?: string; // For images or other assets
  isActive?: boolean; // For admin forms
}

// Vocabulary word definition
export interface VocabularyWord {
  word: string;
  meaning: string;
}

// Word pair for switch card games and matching exercises
export interface WordPair {
  word: string;
  meaning: string;
}

// Type 1: Multiple Choice Questions (including reading comprehension)
export interface MultipleChoiceQuestion extends BaseQuestion {
  type: "1";
  passage?: string; // For reading comprehension passages
  vocabularyWords?: VocabularyWord[]; // Vocabulary definitions
  options: string[];
  correctAnswer: number;
}

// Type 2: Reading Comprehension with Main Idea Selection (from JSON analysis)
export interface ReadingComprehensionQuestion extends BaseQuestion {
  type: "2";
  passage: string;
  vocabularyWords?: VocabularyWord[];
  options: string[];
  correctAnswer: number;
}

// Type 3: Audio Listening with Multiple Selection (Checkboxes)
export interface AudioListeningQuestion extends BaseQuestion {
  type: "3";
  audioUrl: string;
  options: string[];
  correctAnswers: number[]; // Multiple selections possible
}

// Type 4: Sentence Building/Arrangement
export interface SentenceBuildingQuestion extends BaseQuestion {
  type: "4";
  wordOptions: Array<{
    word: string;
    index: number;
    setting: number;
  }>;
}

// Type 6: Reading with Reference Material (like TV Schedule)
export interface ReferenceReadingQuestion extends BaseQuestion {
  type: "6";
  asset: string; // URL to reference image/document (TV schedule, etc.)
  questions: Array<{
    question: string;
    options: string[];
    correctOption: string; // The correct answer as a string
  }>;
  // For backwards compatibility, we'll add these optional fields
  options?: string[]; // Direct options (legacy)
  correctAnswer?: number; // Direct correct answer (legacy)
}

// Type 8: Word Matching (Switch Card Game)
export interface SwitchCardQuestion extends BaseQuestion {
  type: "8";
  wordPairs: WordPair[];
}

// Type 9: Parts of Speech Matching (from JSON analysis)
export interface PartsOfSpeechQuestion extends BaseQuestion {
  type: "9";
  wordPairs: WordPair[];
}

// Type 10: Fill-in-the-Blank Multiple Choice (from JSON analysis)
export interface FillInBlankMCQuestion extends BaseQuestion {
  type: "10";
  questions: Array<{
    question: string;
    options: string[];
    correctOption: string;
    prompt?: string;
    id?: string;
  }>;
}

// Type 11: Speaking Practice (from JSON analysis)
export interface SpeakingPracticeQuestion extends BaseQuestion {
  type: "11";
  audioSrc: string;
  script: string;
  recordingTimeLimit?: number;
}

// Type 12: Instructional Content/Reading Material (from JSON analysis)
export interface InstructionalContentQuestion extends BaseQuestion {
  type: "12";
  content: {
    questionTitle: string;
    answerTitle: string;
    questionContent: Array<{
      text: string;
      isBold: boolean;
    }>;
    answerContent: Array<{
      text: string;
      isBold: boolean;
    }>;
  };
}

// Type 13: True/False Selection (Multiple statements) (from JSON analysis)
export interface TrueFalseSelectionQuestion extends BaseQuestion {
  type: "13";
  statements: Array<{
    text: string;
    isTrue: boolean;
  }>;
  numberOfCorrect: number;
  tip?: string;
}

// Union type for all question types
export type ModuleQuestion =
  | MultipleChoiceQuestion
  | ReadingComprehensionQuestion
  | AudioListeningQuestion
  | SentenceBuildingQuestion
  | ReferenceReadingQuestion
  | SwitchCardQuestion
  | PartsOfSpeechQuestion
  | FillInBlankMCQuestion
  | SpeakingPracticeQuestion
  | InstructionalContentQuestion
  | TrueFalseSelectionQuestion;

export enum ProgramWeekStatus {
  Incomplete = "INCOMPLETE",
  Completed = "COMPLETED",
  InProgress = "IN PROGRESS",
  Locked = "LOCKED",
}

export enum QuestionType {
  MultipleChoice = "1",
  ReadingComprehension = "2",
  AudioListening = "3",
  SentenceBuilding = "4",
  ReferenceReading = "6",
  SwitchCard = "8",
  PartsOfSpeech = "9",
  FillInBlankMC = "10",
  SpeakingPractice = "11",
  InstructionalContent = "12",
  TrueFalseSelection = "13",
}

// Question type names mapping
export const QUESTION_TYPE_NAMES: Record<string, string> = {
  "1": "Multiple Choice Questions",
  "2": "Reading Comprehension (Main Idea)",
  "3": "Audio Listening (Multiple Selection)",
  "4": "Sentence Building/Arrangement",
  "6": "Reading with Reference Material",
  "8": "Word Matching (Switch Card)",
  "9": "Parts of Speech Matching",
  "10": "Fill-in-the-Blank Multiple Choice",
  "11": "Speaking Practice",
  "12": "Instructional Content",
  "13": "True/False Selection",
};

export type MediaType = "audio" | "image";

// Quiz results interfaces
export interface QuizResults {
  score: number;
  totalPoints: number;
  percentage: number;
  correctAnswers: number;
  totalQuestions: number;
  timeSpent: number;
  answers: UserAnswer[];
}

export interface UserAnswer {
  questionId: string;
  answer: string | number | string[]; // Support for different answer types
  isCorrect: boolean;
  points: number;
  timeSpent?: number;
}

// Additional interface for transformed weekly content for UI
export interface UIWeekContent {
  weekName: string;
  status: string;
  progress: string;
  startDate: string;
  endDate: string;
  weekNumber: number;
  weekId: string;
  modules: Module[];
  unlockDate: string;
  isFutureWeek?: boolean;
}

// Type guards for question types
export function isSwitchCardQuestion(
  question: ModuleQuestion
): question is SwitchCardQuestion {
  return question.type === "8";
}

export function isMultipleChoiceQuestion(
  question: ModuleQuestion
): question is MultipleChoiceQuestion {
  return question.type === "1";
}

export function isReadingComprehensionQuestion(
  question: ModuleQuestion
): question is ReadingComprehensionQuestion {
  return question.type === "2";
}

export function isAudioListeningQuestion(
  question: ModuleQuestion
): question is AudioListeningQuestion {
  return question.type === "3";
}

export function isSentenceBuildingQuestion(
  question: ModuleQuestion
): question is SentenceBuildingQuestion {
  return question.type === "4";
}

export function isReferenceReadingQuestion(
  question: ModuleQuestion
): question is ReferenceReadingQuestion {
  return question.type === "6";
}

export function isPartsOfSpeechQuestion(
  question: ModuleQuestion
): question is PartsOfSpeechQuestion {
  return question.type === "9";
}

export function isFillInBlankMCQuestion(
  question: ModuleQuestion
): question is FillInBlankMCQuestion {
  return question.type === "10";
}

export function isSpeakingPracticeQuestion(
  question: ModuleQuestion
): question is SpeakingPracticeQuestion {
  return question.type === "11";
}

export function isInstructionalContentQuestion(
  question: ModuleQuestion
): question is InstructionalContentQuestion {
  return question.type === "12";
}

export function isTrueFalseSelectionQuestion(
  question: ModuleQuestion
): question is TrueFalseSelectionQuestion {
  return question.type === "13";
}
